/**
 * Landing 機能の型定義（Phase 0+ Landing 機能）
 *
 * - sourceCampaignId: campaigns/{id} を参照（単一情報源）
 * - promptInsertion: campaign.background_info の各キーをプロンプトに含めるかのトグル
 * - sections: ランディングを構成する section の配列（順序付き）
 *
 * 公開 URL: /l/{slug} （status === 'published' のみアクセス可）
 */
import type { IsoOrTimestamp } from './common';

/** 10 visual identities */
export type LandingVisualIdentity =
  | 'light'
  | 'dark'
  | 'vibrant'
  | 'pastel'
  | 'editorial'
  | 'mono'
  | 'mint'
  | 'azure'
  | 'terra'
  | 'noir';

/** 業界レシピ */
export type LandingIndustryRecipe =
  | 'saas_b2b'
  | 'consumer_app'
  | 'ecommerce_dtc'
  | 'healthcare_wellness'
  | 'hardware_lineup'
  | 'software_lineup'
  | 'event_promotion';

export type LandingStatus = 'draft' | 'published';

/**
 * 生成モード
 *  - 'recipe': 業界レシピを使う（ASSEMBLY[industry].sequence に従う）
 *  - 'ai_design': AI が業界・ブランドから sequence と variant 自体を設計する
 */
export type LandingGenerationMode = 'recipe' | 'ai_design';

export type LandingSectionStatus = 'ready' | 'regenerating' | 'needs_review' | 'draft';

/** sections 配列の各要素のメタ（軽量、content は別 doc） */
export interface LandingSectionRef {
  id: string;
  type: string; // SectionType (hero / feature_grid / ...)
  variant: string;
  order: number;
  status: LandingSectionStatus;
  /** 一覧表示用に最初の見出しを denormalize（titleManual=true のときは手入力の名前） */
  title?: string;
  /** true = 画面から手で名前を付けた。AI 再生成・AI 複製で title を上書きしない */
  titleManual?: boolean;
  /** true = 画面から手でステータスを選んだ。1 セクション再生成で status を上書きしない */
  statusManual?: boolean;
  /** 概算 word count（全体集計用） */
  wordCount?: number;
  /**
   * 非表示フラグ。true のとき:
   *   - 公開ページではセクションを完全に除外（DOM 自体出力しない）
   *   - エディタではフェードアウト表示
   * 未指定 = 表示。
   */
  hidden?: boolean;
}

/** assignedCampaigns: 各種 ID をアサイン */
export interface LandingAssignedCampaigns {
  formIds: string[];
  mailRecipientIds: string[];
  lineNewsIds: string[];
}

/** ランディング個別の Google タグ。head/body いずれか非空でグローバル既定を上書き。 */
export interface LandingGoogleTag {
  /** <head> 内に注入する生コード。 */
  headHtml: string;
  /** <body> 直下に注入する生コード。 */
  bodyHtml: string;
  /**
   * オプトアウト。true のとき、この LP には一切タグを注入しない。
   * グローバル既定が有効でもこの LP だけ除外し、headHtml/bodyHtml も無視する。
   * この LP の CTA（?lp=<id>）から開いたフォームも同様に除外される。
   */
  disabled?: boolean;
}

export interface LandingBrandSettings {
  logoStoragePath: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  headingFont: string | null;
  bodyFont: string | null;
  socialLinks: Record<string, string>;
}

/** /landings/{landingId} */
export interface Landing {
  ownerId: string;
  title: string;
  /** 公開URL用 (一意) */
  slug: string;
  status: LandingStatus;

  // ── 生成入力ソース ──
  sourceCampaignId: string;
  /**
   * background_info の各キーをプロンプトに含めるかのトグル。
   * 動的キー対応。デフォルト ON: description / goal / targetSegment / kpi
   */
  promptInsertion: Record<string, boolean>;

  // ── デザイン設定 ──
  brand: string;
  industry: LandingIndustryRecipe;
  /**
   * 生成モード。デフォルト 'recipe'（業界レシピの sequence に従う）。
   * 'ai_design' の場合は AI が sequence・variant 自体を設計する。
   */
  generationMode?: LandingGenerationMode;
  visualIdentity: LandingVisualIdentity;
  domain: string;
  brandSettings: LandingBrandSettings;

  // ── 構造 ──
  sections: LandingSectionRef[];
  assignedCampaigns: LandingAssignedCampaigns;

  /**
   * この LP 個別の Google タグ（GTM 等）。
   * headHtml / bodyHtml いずれかが非空なら、グローバル既定 (settings/googleTag) を上書きして
   * この LP と、この LP の CTA（?lp=<id>）から開いたフォームに適用される。
   * 未設定 / 両方空ならグローバル既定にフォールバック。
   */
  googleTag?: LandingGoogleTag;

  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
  publishedAt: IsoOrTimestamp | null;
}

/**
 * セクション単位の表示スタイル設定。
 * AI 再生成では更新せず、ユーザーが明示的に切り替えた値を維持する。
 */
export interface SectionStyle {
  /** CTA ボタンの整列（hero / cta_banner で有効。他セクションは将来拡張用） */
  ctaAlign?: 'left' | 'center' | 'right';
  /** ロゴ表示位置（hero セクションで有効） */
  logoPosition?: 'none' | 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** ロゴサイズ（hero セクションで有効） */
  logoSize?: 'sm' | 'md' | 'lg';
}

/** /landings/{landingId}/sections/{sectionId} */
export interface LandingSectionDoc {
  type: string;
  variant: string;
  order: number;
  /** schema-validated content。動的構造 */
  content: Record<string, unknown>;
  /** 画像フィールドの Storage パス（再生成時にテキストのみ更新するため別管理） */
  imageRefs: Record<string, string>;
  /** 表示スタイル設定（content と分離。AI 再生成で上書きされない） */
  style?: SectionStyle;
  updatedAt: IsoOrTimestamp;
}

/** /landings/{landingId}/sections/{sectionId}/comments/{commentId} */
export interface LandingComment {
  authorId: string;
  body: string;
  resolved: boolean;
  createdAt: IsoOrTimestamp;
}

/** /landings/{landingId}/history/{historyId} */
export interface LandingHistoryEntry {
  kind: 'generate' | 'regenerate_all' | 'regenerate_section' | 'regenerate_field' | 'publish';
  /** そのときの sections / promptInsertion / brief のスナップショット */
  snapshot: Record<string, unknown>;
  /** 実際に Claude に送ったプロンプト（再現用） */
  promptUsed?: string;
  /** そのときの campaign.background_info のスナップショット（campaign が変わっても再現可能に） */
  campaignSnapshot?: Record<string, unknown>;
  /** 影響を受けた sectionId（regenerate_section / regenerate_field のみ） */
  targetSectionId?: string;
  targetFieldPath?: string;
  /** 生成時に AI に渡した参考資料のファイル名（本文は保存しない） */
  referenceFiles?: string[];
  createdAt: IsoOrTimestamp;
  createdBy: string;
}

/**
 * ランディング生成時に添付する参考資料（PDF / Word / Excel / CSV）。
 * ブラウザでファイルから文字を取り出し、生成リクエストに同梱して送る。
 * Storage / Firestore には保存しない（履歴にはファイル名のみ残す）。
 */
export type LandingReferenceKind = 'pdf' | 'docx' | 'xlsx' | 'csv';

export interface LandingReferenceDocument {
  name: string;
  kind: LandingReferenceKind;
  /** AI に渡す本文（上限で切り詰め済み） */
  text: string;
  /** 上限を超えたため途中で切り詰めたか */
  truncated?: boolean;
}

/** 参考資料の上限 */
export const LANDING_REFERENCE_MAX_FILES = 5;
export const LANDING_REFERENCE_MAX_FILE_MB = 10;
/** 1 ファイルあたり AI に渡す最大文字数 */
export const LANDING_REFERENCE_MAX_CHARS_PER_FILE = 20_000;
/** 全ファイル合計で AI に渡す最大文字数 */
export const LANDING_REFERENCE_MAX_TOTAL_CHARS = 60_000;

/** デフォルト 7 キーのプロンプト挿入トグル既定値 */
export const LANDING_DEFAULT_PROMPT_INSERTION: Record<string, boolean> = {
  description: true,
  goal: true,
  targetSegment: true,
  budget: false,
  kpi: true,
  owner: false,
  memo: false,
};

export const LANDING_VISUAL_IDENTITIES: LandingVisualIdentity[] = [
  'light',
  'dark',
  'vibrant',
  'pastel',
  'editorial',
  'mono',
  'mint',
  'azure',
  'terra',
  'noir',
];

export const LANDING_INDUSTRY_RECIPES: LandingIndustryRecipe[] = [
  'saas_b2b',
  'consumer_app',
  'ecommerce_dtc',
  'healthcare_wellness',
  'hardware_lineup',
  'software_lineup',
  'event_promotion',
];

export const LANDING_INDUSTRY_LABELS: Record<LandingIndustryRecipe, string> = {
  saas_b2b: 'SaaS / B2B サービス',
  consumer_app: 'コンシューマーアプリ',
  ecommerce_dtc: 'EC / DTC ブランド',
  healthcare_wellness: 'ヘルスケア / ウェルネス',
  hardware_lineup: 'ハードウェア製品',
  software_lineup: 'ソフトウェア製品',
  event_promotion: 'イベント告知',
};

/** slug の正規化 / 検証 */
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

export function isValidSlug(slug: string): boolean {
  return SLUG_RE.test(slug);
}

export function normalizeSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 64);
}
